import type { GridRowId } from '@mui/x-data-grid-pro';

export interface GroupBlockAnchor {
  id: GridRowId;
  type: 'before' | 'after';
}

export interface GroupBlockMove {
  orderedIds: GridRowId[];
  finalOrder: GridRowId[];
  anchor: GroupBlockAnchor;
}

export interface RowPlacement {
  id: GridRowId;
  targetId: GridRowId;
  position: 'above' | 'below';
}

const idSet = (ids: Iterable<GridRowId>): Set<string> => new Set(Array.from(ids, String));

export const computeGroupBlockMove = (
  siblingIds: readonly GridRowId[],
  selectedIds: Iterable<GridRowId>,
  dropTarget: { rowId: GridRowId; position: 'above' | 'below' },
): GroupBlockMove | null => {
  const selected = idSet(selectedIds);
  const isSelected = (id: GridRowId) => selected.has(String(id));

  const targetIdx = siblingIds.findIndex((id) => String(id) === String(dropTarget.rowId));
  if (targetIdx === -1) return null;

  const orderedIds = siblingIds.filter(isSelected);
  const unselected = siblingIds.filter((id) => !isSelected(id));
  if (orderedIds.length === 0 || unselected.length === 0) return null;

  const lineIdx = dropTarget.position === 'below' ? targetIdx + 1 : targetIdx;
  const insertAt = siblingIds.slice(0, lineIdx).filter((id) => !isSelected(id)).length;
  const finalOrder = [
    ...unselected.slice(0, insertAt),
    ...orderedIds,
    ...unselected.slice(insertAt),
  ];

  if (finalOrder.every((id, i) => String(id) === String(siblingIds[i]))) return null;

  const before = unselected[insertAt - 1];
  const anchor: GroupBlockAnchor =
    before !== undefined
      ? { id: before, type: 'after' }
      : { id: unselected[0] as GridRowId, type: 'before' };

  return { orderedIds, finalOrder, anchor };
};

export const toRowPlacements = (
  targetOrder: readonly GridRowId[],
  movingIds: Iterable<GridRowId>,
): RowPlacement[] => {
  const moving = idSet(movingIds);
  const firstStatic = targetOrder.find((id) => !moving.has(String(id)));
  if (firstStatic === undefined) return [];

  return targetOrder.flatMap((id, i): RowPlacement[] => {
    if (!moving.has(String(id))) return [];
    const previous = targetOrder[i - 1];
    return previous === undefined
      ? [{ id, targetId: firstStatic, position: 'above' }]
      : [{ id, targetId: previous, position: 'below' }];
  });
};
